import { Check } from "lucide-react";
import { Counter } from "@/components/effects";

const ROWS = [
  { item: "Claude Pro / Max", cost: "$200/mo", note: "We carry this", carried: true },
  { item: "Codex Plus (or equivalent)", cost: "$200/mo", note: "We carry this", carried: true },
  { item: "App hosting + DB + CDN", cost: "$100/mo", note: "Customer keeps theirs; we deploy to it", carried: true },
  { item: "Uptime + error monitoring (UptimeRobot / Sentry)", cost: "$100–250/mo", note: "We wire it during setup", carried: true },
  { item: "CI/CD platform (GitHub Actions paid, Vercel Pro)", cost: "$50–200/mo", note: "Replaced by our deploy pipeline", carried: true },
  { item: "Secrets management (Doppler / 1Password)", cost: "$50–100/mo", note: "Setup fee includes vault migration", carried: true },
  { item: "Backup + verified restore", cost: "$50–150/mo", note: "Nightly offsite + tested restore", carried: true },
  { item: "AI code review (Greptile, CodeRabbit)", cost: "$30–100/mo", note: "We review every commit", carried: true },
  { item: "Part-time engineer", cost: "$3,000–5,000/mo", note: "This is the big one", carried: true },
];

export default function Savings() {
  return (
    <section
      id="savings"
      data-testid="savings-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            §08 · Savings
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-end mb-12">
          <h2 className="lg:col-span-8 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug">
            What you'd spend doing this yourself.
            <br />
            <span className="text-zinc-500">(And how we replace it.)</span>
          </h2>
          <p className="lg:col-span-4 text-base leading-relaxed text-zinc-400 font-light">
            Real, honest line items most teams accumulate trying to ship software
            the responsible way. We carry the AI subscriptions, replace the tooling,
            and free you from the part-time engineer.
          </p>
        </div>

        {/* Table */}
        <div
          data-testid="savings-table"
          className="border border-white/10 overflow-x-auto"
        >
          {/* Header row */}
          <div
            role="row"
            className="grid grid-cols-12 gap-px bg-white/10 border-b border-white/10 min-w-[520px]"
          >
            <div className="col-span-8 bg-[#0a0a0a] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400">
              Line item
            </div>
            <div className="col-span-4 bg-[#0a0a0a] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400">
              Self-serve cost
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((r, i) => (
            <div
              key={r.item}
              role="row"
              data-testid={`savings-row-${i}`}
              className={`grid grid-cols-12 gap-px bg-white/10 min-w-[520px] ${i === ROWS.length - 1 ? "" : "border-b border-white/10"}`}
            >
              <div className="col-span-8 bg-[#050505] px-6 py-5 flex items-center gap-3 text-sm sm:text-base text-white font-light leading-snug">
                {r.carried ? (
                  <Check className="w-4 h-4 text-yellow-400 shrink-0" strokeWidth={2} />
                ) : (
                  <span className="w-4 h-4 shrink-0" />
                )}
                {r.item}
              </div>
              <div className="col-span-4 bg-[#050505] px-6 py-5 font-mono text-sm text-zinc-300 flex items-center">
                {r.cost}
              </div>
            </div>
          ))}

          {/* Total row */}
          <div className="grid grid-cols-12 gap-px bg-yellow-400/30 border-t border-yellow-400/40 min-w-[520px]">
            <div className="col-span-8 bg-[#0a0a0a] px-6 py-5 font-heading text-base sm:text-lg font-light text-white tracking-tight">
              Estimated self-serve total
            </div>
            <div className="col-span-4 bg-[#0a0a0a] px-6 py-5 font-heading text-lg sm:text-xl font-light text-yellow-400 tracking-tight">
              $3,780–6,300/mo
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-10 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#050505] p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Doing it yourself
            </div>
            <div className="font-heading text-3xl font-light text-zinc-300">
              $<Counter value={3780} />–<Counter value={6300} /><span className="text-base text-zinc-500 font-mono">/mo</span>
            </div>
          </div>
          <div className="bg-[#050505] p-6 ring-1 ring-yellow-400/40">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-yellow-400 mb-2">
              Standard plan with us
            </div>
            <div className="font-heading text-3xl font-light text-yellow-400">
              $<Counter value={1500} /><span className="text-base text-zinc-500 font-mono">/mo</span>
            </div>
          </div>
          <div className="bg-[#050505] p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Monthly savings (avg)
            </div>
            <div className="font-heading text-3xl font-light text-white">
              ~$<Counter value={3540} /><span className="text-base text-zinc-500 font-mono">/mo</span>
            </div>
          </div>
        </div>

        <p className="mt-6 font-mono text-[11px] text-zinc-500 max-w-2xl">
          *Ranges are typical SMB pricing as of Dec 2025. Your mileage may vary —
          but you'll almost certainly spend more on tooling alone than on us.
        </p>
      </div>
    </section>
  );
}
